import dayjs from 'dayjs';
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { useTabManager } from '../../app-v2/context/tab-manager-context';
import { useFolderTagsByLimit, useSubSystemsById } from '../hooks/folders';

import {
  LimitConfig,
  LimitMatchType,
  MatchType,
  SubSystem,
  SystemsWithStates,
  Tag,
  ViewType,
} from '@controlrooms/models';

interface ContextProps {
  folder: SubSystem | undefined;
  tag: Tag | undefined;
  filterString: string;
  limitMatchType: LimitMatchType;
  stateTags: Tag[];
  defaultLimit: LimitConfig;
  selectedLimitId: string | undefined;
  updateTagSelection: (
    folder: SubSystem,
    tag: Tag,
    systemsWithStates?: SystemsWithStates[],
  ) => void;
  setEditableLimit: Dispatch<SetStateAction<LimitConfig | undefined>>;
  setFilterString: Dispatch<SetStateAction<string>>;
  setLimitMatchType: Dispatch<SetStateAction<LimitMatchType>>;
  editableLimit: LimitConfig | undefined;
  matchType: MatchType;
  setMatchType: Dispatch<SetStateAction<MatchType>>;
  updateSelectedLimitId: (limitId: string | undefined) => void;
}

const dateFormat = 'YYYY-MM-DDTHH:mm:ss';
const defaultEndDate = '2050-12-30T23:59:59';

const defaultLimit: LimitConfig = {
  limit_id: 0,
  tag_name: '',
  tag_display_name: '',
  start_time: dayjs().format(dateFormat),
  end_time: defaultEndDate,
  low_low_limit: null,
  low_limit: null,
  high_limit: null,
  high_high_limit: null,
  criticality: 'Low',
  limit_type: 'Numeric',
  is_active: true,
  states: [],
};

const defaultState: ContextProps = {
  tag: undefined,
  defaultLimit,
  stateTags: [],
  limitMatchType: LimitMatchType.WITH_LIMITS,
  setLimitMatchType: () => undefined,
  matchType: MatchType.ANY,
  setMatchType: () => undefined,
  filterString: '',
  setFilterString: () => undefined,
  folder: undefined,
  updateTagSelection: () => undefined,
  setEditableLimit: () => undefined,
  editableLimit: undefined,
  selectedLimitId: undefined,
  updateSelectedLimitId: () => undefined,
};

export const LimitContext = createContext<ContextProps>(defaultState);

const LimitContextProvider: React.FC = ({ children }) => {
  const { data: folderTags } = useFolderTagsByLimit();
  const { data: systemData } = useSubSystemsById();
  const { activeViewState, updateActiveTabViewState } = useTabManager();
  const limitManagerView = activeViewState?.view[ViewType.LIMIT_MANAGER];

  const [filterString, setFilterString] = useState('');
  const [matchType, setMatchType] = useState<MatchType>(MatchType.ANY);
  const [limitMatchType, setLimitMatchType] = useState<LimitMatchType>(LimitMatchType.WITH_LIMITS);
  const [editableLimit, setEditableLimit] = useState<LimitConfig | undefined>();

  // Derive folder and tag from viewstate + data
  const folder = useMemo(() => {
    const folderId = limitManagerView?.folder;
    if (folderId == null || !systemData) return undefined;
    return systemData[folderId] as SubSystem | undefined;
  }, [limitManagerView?.folder, systemData]);

  const tag = useMemo(() => {
    const folderId = limitManagerView?.folder;
    const tagName = limitManagerView?.tag;
    if (folderId == null || !tagName || !folderTags) return undefined;
    return (folderTags[folderId] || []).find((t: Tag) => t.name === tagName);
  }, [limitManagerView?.folder, limitManagerView?.tag, folderTags]);

  const selectedLimitId = limitManagerView?.limit_id;

  // Derive stateTags from folder + folderTags (systemsWithStates stored in closure via updateTagSelection)
  const [stateTags, setStateTags] = useState<Tag[]>([]);

  const updateSelectedLimitId = useCallback(
    (limitId: string | undefined) => {
      if (!activeViewState || !updateActiveTabViewState) return;
      updateActiveTabViewState({
        ...activeViewState,
        isDirty: true,
        view: {
          ...activeViewState.view,
          [ViewType.LIMIT_MANAGER]: {
            ...activeViewState.view[ViewType.LIMIT_MANAGER],
            limit_id: limitId,
          },
        },
      }).catch(() => undefined);
    },
    [activeViewState, updateActiveTabViewState],
  );

  const updateTagSelection = useCallback(
    (selectedFolder: SubSystem, selectedTag: Tag, systemsWithStates?: SystemsWithStates[]) => {
      setEditableLimit(undefined);

      // Compute state tags
      const systemStateTags =
        systemsWithStates?.find(
          (sys: SystemsWithStates) => sys.infra_node_id === selectedFolder.folder,
        )?.state_tag_names ?? [];

      const matchedStateTags = (folderTags?.[selectedFolder.folder] ?? []).filter((t: Tag) =>
        systemStateTags.includes(t.name),
      );
      setStateTags(matchedStateTags);

      // Persist to viewstate - this drives derived folder/tag
      if (activeViewState && updateActiveTabViewState) {
        updateActiveTabViewState({
          ...activeViewState,
          isDirty: true,
          view: {
            ...activeViewState.view,
            [ViewType.LIMIT_MANAGER]: {
              ...activeViewState.view[ViewType.LIMIT_MANAGER],
              folder: selectedFolder.folder,
              tag: selectedTag.name,
              limit_id: undefined, // reset limit selection on tag change
            },
          },
        }).catch(() => undefined);
      }
    },
    [activeViewState, updateActiveTabViewState, folderTags],
  );

  const appState = useMemo(
    () => ({
      folder,
      tag,
      defaultLimit,
      stateTags,
      filterString,
      setFilterString,
      updateTagSelection,
      editableLimit,
      setEditableLimit,
      limitMatchType,
      setLimitMatchType,
      matchType,
      setMatchType,
      selectedLimitId,
      updateSelectedLimitId,
    }),
    [
      editableLimit,
      filterString,
      folder,
      limitMatchType,
      matchType,
      stateTags,
      tag,
      updateTagSelection,
      selectedLimitId,
      updateSelectedLimitId,
    ],
  );

  return <LimitContext.Provider value={appState}>{children}</LimitContext.Provider>;
};

export default LimitContextProvider;
